// Generic modal dialog component. Pass `title` (string) and `body` (HTML string
// or DOM element) plus optional `actions` (array of {label, variant, onClick}).
// Closes on ESC, backdrop click, or any action that returns truthy.

import { Component, esc } from '../core/component.js';
import { t } from '../core/i18n.js';

export class Modal extends Component {
  constructor(props = {}) {
    super(props);
    this.state = { open: true };
    this._onKey = this._onKey.bind(this);
  }

  template() {
    if (!this.state.open) return '<div hidden></div>';
    const { title, body = '', actions = [] } = this.props;
    const bodyHtml = typeof body === 'string' ? body : '';
    const actionsHtml = actions.map((a, i) => {
      const variant = a.variant === 'primary' ? 'btn-primary'
        : a.variant === 'danger' ? 'btn-danger'
        : 'btn-ghost';
      return `<button type="button" class="btn ${variant}" data-modal-action="${i}">${esc(a.label)}</button>`;
    }).join('');
    return `
      <div class="modal-backdrop" data-modal-root role="presentation">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <header class="modal-header">
            <h3 id="modal-title">${esc(title || '')}</h3>
            <button type="button" class="btn btn-ghost btn-icon" data-modal-close aria-label="${esc(t('common.close'))}">×</button>
          </header>
          <div class="modal-body" data-modal-body>${bodyHtml}</div>
          ${actions.length > 0 ? `<footer class="modal-footer">${actionsHtml}</footer>` : ''}
        </div>
      </div>
    `;
  }

  mount() {
    if (!this.state.open) return;
    if (typeof this.props.body !== 'string' && this.props.body) {
      const slot = this.el.querySelector('[data-modal-body]');
      if (slot) slot.replaceChildren(this.props.body);
    }
    this.el.querySelector('[data-modal-close]')?.addEventListener('click', () => this.close());
    this.el.querySelector('[data-modal-root]')?.addEventListener('click', (e) => {
      if (e.target.matches('[data-modal-root]')) this.close();
    });
    this.el.querySelectorAll('[data-modal-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const i = Number(btn.getAttribute('data-modal-action'));
        const action = (this.props.actions || [])[i];
        if (!action || typeof action.onClick !== 'function') { this.close(); return; }
        const result = await action.onClick();
        if (result !== false) this.close();
      });
    });
    document.addEventListener('keydown', this._onKey);
  }

  unmount() {
    document.removeEventListener('keydown', this._onKey);
  }

  _onKey(e) {
    if (e.key === 'Escape') this.close();
  }

  close() {
    if (typeof this.props.onClose === 'function') this.props.onClose();
    this.setState({ open: false });
    this.destroy();
  }
}

// Convenience helper: appends a modal to <body> and returns the instance.
export function showModal(props) {
  const m = new Modal(props);
  const host = document.createElement('div');
  document.body.appendChild(host);
  m.render(host);
  const origClose = m.close.bind(m);
  m.close = () => { origClose(); if (host.parentNode) host.parentNode.removeChild(host); };
  return m;
}
